import React, { useEffect, useState, useRef } from "react";
import {
	Stack,
	Typography,
	Grid,
	Checkbox,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	CircularProgress,
	Snackbar,
	Alert
} from "@mui/material";
import { ContainedSearchButton } from "../../../components/Buttons";
import { StyledTextBox } from "../../../components/TextBox";
import { useParams } from "react-router-dom";
import {
	GetClientInformation,
	UpdateClientInformation,
} from "../../../services/client.services";
import { LoadingBox } from "../../../components/Box";

export default function EditClientInformation() {
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [formFields, setFormFields] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const fetchData = async () => {
			setIsLoading(true);
			try {
				const data = await GetClientInformation(id);
				setFormFields({
					clientType: { value: data.clientType || "", error: false, message: "" },
					username: { value: data.username || "", error: false, message: "" },
					email: { value: data.email || "", error: false, message: "" },
					firstName: { value: data.firstName || "", error: false, message: "" },
					lastName: { value: data.lastName || "", error: false, message: "" },
					contactNo: { value: data.contactNo || "", error: false, message: "" },
					address: { value: data.address || "", error: false, message: "" },
					country: { value: data.country || "", error: false, message: "" },
					currency: { value: data.currency || "", error: false, message: "" },
					timezone: { value: data.timezone || "", error: false, message: "" },
					id: { value: data.uniqueId || "", error: false, message: "" },
				});
			} catch (error) {
				console.error("Error fetching client data", error);
				setSnackbar({ open: true, message: "Failed to fetch client info.", severity: 'error' });
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [id]);

	const updateField = (field, value) => {
		setFormFields((prev) => ({
			...prev,
			[field]: {
				...prev[field],
				value,
				error: false,
				message: "",
			},
		}));
	};

	const validateFields = () => {
		let hasError = false;
		const requiredFields = ["username", "email", "firstName", "lastName"];
		const updatedFields = { ...formFields };

		requiredFields.forEach((field) => {
			if (!formFields[field].value.trim()) {
				updatedFields[field].error = true;
				updatedFields[field].message = "This field is required";
				hasError = true;
			}
		});

		setFormFields(updatedFields);
		return !hasError;
	};

	const handleSave = async () => {
		if (!validateFields()) return;

		const payload = {
			id: id,
			clientType: formFields.clientType.value.toUpperCase(),
			username: formFields.username.value,
			email: formFields.email.value,
			firstName: formFields.firstName.value,
			lastName: formFields.lastName.value,
			contactNo: formFields.contactNo.value,
			address: formFields.address.value,
			country: formFields.country.value,
			currency: formFields.currency.value,
			timezone: formFields.timezone.value,
		};

		try {
			setIsLoading(true);
			await UpdateClientInformation(payload);
			setSnackbar({ open: true, message: "Client information updated successfully!", severity: 'success' });
		} catch (error) {
			console.error("Failed to update client", error);
			setSnackbar({ open: true, message: "Update failed. Try again later.", severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	if (!formFields || isLoading) {
		return (
			<LoadingBox>
				<CircularProgress />
			</LoadingBox>
		);
	}

	return (
		<div>
			{/* Client Type */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Client Type
						</Typography>
					</Grid>
					<Grid xs={9}>
						<FormControl>
							<RadioGroup
								row
								value={formFields.clientType.value.toUpperCase()}
								onChange={(e) => updateField("clientType", e.target.value)}
							>
								<FormControlLabel
									value="SELLER"
									control={<Radio size="small" />}
									label="Seller"
								/>
								<FormControlLabel
									value="PARTNER"
									control={<Radio size="small" />}
									label="Partner"
								/>
							</RadioGroup>
						</FormControl>
					</Grid>
				</Grid>
			</Grid>

			<br />
			{/* Row 1 */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">User ID</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox fullWidth disabled value={formFields.id.value} />
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Username</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.username.value}
							onChange={(e) => updateField("username", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			{/* Row 2 */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">First Name</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.firstName.value}
							onChange={(e) => updateField("firstName", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Last Name</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.lastName.value}
							onChange={(e) => updateField("lastName", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			{/* Row 3 */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Email</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.email.value}
							onChange={(e) => updateField("email", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Address</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.address.value}
							onChange={(e) => updateField("address", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			{/* Row 4 */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Country</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.country.value}
							onChange={(e) => updateField("country", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Contact</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.contactNo.value}
							onChange={(e) => updateField("contactNo", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			{/* Row 5 */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Currency</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.currency.value}
							onChange={(e) => updateField("currency", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Timezone</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.timezone.value}
							onChange={(e) => updateField("timezone", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			<Stack direction="row" spacing={1} alignItems="center">
				<Checkbox

				/>
				<Typography variant="body2">
					Notify the changes for user.
				</Typography>
			</Stack>

			<br />
			<ContainedSearchButton variant="contained" onClick={handleSave}>
				Save changes
			</ContainedSearchButton>
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
